import { useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { Button, Modal, Select, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { SOURCE_LABEL, SOURCE_ORDER } from '../labels'
import { useUploadDocument } from '../hooks'

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx'
const MAX_MB = 50

/** Modal „Prześlij skan" — drag & drop + wybór źródła wpływu. */
export function UploadModal({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean
  onClose: () => void
  onUploaded?: (documentId: number) => void
}) {
  const toast = useToast()
  const upload = useUploadDocument()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [source, setSource] = useState<string>('scan')
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setFile(null)
    setError(null)
    setDragging(false)
  }

  const pick = (candidate: File | undefined) => {
    if (!candidate) return
    if (candidate.size > MAX_MB * 1024 * 1024) {
      setError(`Plik przekracza ${MAX_MB} MB.`)
      return
    }
    setError(null)
    setFile(candidate)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    pick(event.dataTransfer.files[0])
  }

  const submit = async () => {
    if (!file) return
    try {
      const created = await upload.mutateAsync({ file, source })
      if (created.duplicate) {
        toast.push({
          title: 'Dokument już był w skrzynce',
          description: 'Plik o tej samej sumie kontrolnej oznaczono jako duplikat.',
          variant: 'warning',
        })
      } else {
        toast.success('Dokument przyjęty — trwa analiza AI')
      }
      reset()
      onClose()
      onUploaded?.(created.id)
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się przesłać pliku.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Prześlij skan"
      subtitle="PDF, PNG, JPG albo DOCX — do 50 MB. Dokument trafi do kolejki OCR i analizy AI."
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Anuluj
          </Button>
          <Button variant="primary" onClick={submit} disabled={!file} loading={upload.isPending}>
            Prześlij
          </Button>
        </>
      }
    >
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter') inputRef.current?.click()
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded border border-dashed px-6 py-10 text-center transition-colors duration-fast',
          dragging ? 'border-navy-600 bg-[#f2f6f9]' : 'border-line bg-rowhover hover:border-navy-600',
        )}
      >
        <span className="text-base font-medium text-ink">
          {file ? file.name : 'Przeciągnij plik albo kliknij, aby wybrać'}
        </span>
        <span className="text-small text-muted">
          {file
            ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
            : 'Obsługiwane formaty: PDF, PNG, JPG, DOCX'}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(event) => pick(event.target.files?.[0])}
        />
      </div>

      <div className="mt-4 max-w-[240px]">
        <Select
          label="Źródło wpływu"
          value={source}
          onChange={(event) => setSource(event.target.value)}
          options={SOURCE_ORDER.map((value) => ({ value, label: SOURCE_LABEL[value] }))}
        />
      </div>

      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
